import { Epic, ofType } from "redux-observable";
import { catchError, debounceTime, map, switchMap } from "rxjs/operators";
import { handleError } from "../../../../redux/error.redux";
import { FeatureSet } from "../../../../models/Offerings/featureSet";
import { offeringsService } from "../../../../http/offerings.service";
import { CustomerPortalState } from "../index";
import { createSelector } from "reselect";
import { IFeatureSet } from "@nendaTypes/NendaTypes";

export interface OfferingsState {
  isLoading: boolean;
  featureSets: IFeatureSet[];
  featureSet?: IFeatureSet;
}

export const initialState: OfferingsState = {
  isLoading: false,
  featureSets: [],
  featureSet: undefined,
};

export enum OFFERINGS_ACTION {
  //feature sets
  GET_FEATURE_SETS = "GET_FEATURE_SETS",
  SET_FEATURE_SETS = "SET_FEATURE_SETS",
  GET_FEATURE_SET = "GET_FEATURE_SET",
  SET_FEATURE_SET = "SET_FEATURE_SET",
  CLEAR_FEATURE_SET = "CLEAR_FEATURE_SET",
  CREATE_FEATURE_SET = "CREATE_FEATURE_SET",
  UPDATE_FEATURE_SET = "UPDATE_FEATURE_SET",
  DELETE_FEATURE_SET = "DELETE_FEATURE_SET",
}

export interface OfferingsAction {
  type: OFFERINGS_ACTION;
  payload?: any;
}

// -- Action Creators --
//feature sets
export function GetFeatureSetsAction(searchQuery?: string): OfferingsAction {
  return { type: OFFERINGS_ACTION.GET_FEATURE_SETS, payload: { searchQuery } };
}

export function SetFeatureSetsAction(
  featureSets: IFeatureSet[]
): OfferingsAction {
  return { type: OFFERINGS_ACTION.SET_FEATURE_SETS, payload: { featureSets } };
}

export function GetFeatureSetAction(id: string): OfferingsAction {
  return { type: OFFERINGS_ACTION.GET_FEATURE_SET, payload: { id } };
}

export function SetFeatureSetAction(featureSet: IFeatureSet): OfferingsAction {
  return { type: OFFERINGS_ACTION.SET_FEATURE_SET, payload: { featureSet } };
}

export function ClearFeatureSetAction(): OfferingsAction {
  return { type: OFFERINGS_ACTION.CLEAR_FEATURE_SET };
}

export function CreateFeatureSetAction(name: string): OfferingsAction {
  return { type: OFFERINGS_ACTION.CREATE_FEATURE_SET, payload: { name } };
}

export function UpdateFeatureSetAction(
  featureSet: IFeatureSet
): OfferingsAction {
  return { type: OFFERINGS_ACTION.UPDATE_FEATURE_SET, payload: { featureSet } };
}

export function DeleteFeatureSetAction(id: string): OfferingsAction {
  return { type: OFFERINGS_ACTION.DELETE_FEATURE_SET, payload: { id } };
}

// -- selectors --
export const getFeatureSetsSelector = createSelector(
  [(state: CustomerPortalState) => state.offerings.featureSets],
  (featureSets = []) => featureSets
);

export const getFeatureSetSelector = createSelector(
  [(state: CustomerPortalState) => state.offerings.featureSet],
  (featureSet) => featureSet
);

// -- Reducer --
export default function offeringsReducer(
  state = initialState,
  action: OfferingsAction
): OfferingsState {
  switch (action.type) {
    case OFFERINGS_ACTION.GET_FEATURE_SET:
    case OFFERINGS_ACTION.GET_FEATURE_SETS:
    case OFFERINGS_ACTION.UPDATE_FEATURE_SET:
      return {
        ...state,
        isLoading: true,
      };
    case OFFERINGS_ACTION.SET_FEATURE_SET:
      return {
        ...state,
        featureSet: action.payload.featureSet,
        isLoading: false,
      };
    case OFFERINGS_ACTION.CLEAR_FEATURE_SET:
      return {
        ...state,
        featureSet: undefined,
      };
    case OFFERINGS_ACTION.SET_FEATURE_SETS:
      return {
        ...state,
        featureSets: action.payload.featureSets,
        isLoading: false,
      };
    default:
      return state;
  }
}

// -- Epics --
//feature sets
const getFeaturesSets$: Epic = (action$) => {
  return action$.pipe(
    ofType(OFFERINGS_ACTION.GET_FEATURE_SETS),
    debounceTime(200),
    switchMap((a) => {
      return offeringsService.getFeatureSets(a.payload.searchQuery).pipe(
        map((featureSets) => {
          return SetFeatureSetsAction(featureSets);
        }),
        catchError(handleError)
      );
    })
  );
};

const getFeaturesSet$: Epic = (action$) => {
  return action$.pipe(
    ofType(OFFERINGS_ACTION.GET_FEATURE_SET),
    debounceTime(200),
    switchMap((a) => {
      return offeringsService.getFeatureSet(a.payload.id).pipe(
        map((featureSet) => {
          return SetFeatureSetAction(featureSet);
        }),
        catchError(handleError)
      );
    })
  );
};

const createFeatureSet$: Epic = (action$) => {
  return action$.pipe(
    ofType(OFFERINGS_ACTION.CREATE_FEATURE_SET),
    debounceTime(200),
    switchMap((a) => {
      return offeringsService.createFeatureSet(a.payload.name).pipe(
        map((featureSet) => {
          return SetFeatureSetAction(featureSet);
        }),
        catchError(handleError)
      );
    })
  );
};

const updateFeatureSet$: Epic = (action$) => {
  return action$.pipe(
    ofType(OFFERINGS_ACTION.UPDATE_FEATURE_SET),
    debounceTime(200),
    switchMap((a) => {
      return offeringsService.updateFeatureSet(a.payload.featureSet).pipe(
        map((featureSet) => {
          return SetFeatureSetAction(featureSet);
        }),
        catchError(handleError)
      );
    })
  );
};

const deleteFeatureSet$: Epic = (action$) => {
  return action$.pipe(
    ofType(OFFERINGS_ACTION.DELETE_FEATURE_SET),
    debounceTime(200),
    switchMap((a) => {
      return offeringsService.deleteFeatureSet(a.payload.id).pipe(
        map(() => {
          return ClearFeatureSetAction();
        }),
        catchError(handleError)
      );
    })
  );
};

export const offeringsEpics = [
  getFeaturesSets$,
  getFeaturesSet$,
  createFeatureSet$,
  updateFeatureSet$,
  deleteFeatureSet$,
];
